import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, type ApiResponse } from "@/api/client";
import type { Order, OrderStatus } from "@ckgo2/shared/types";
import { formatPrice } from "@ckgo2/shared/utils";
import { formatDate } from "@/lib/format";
import { Calendar, Users, GripVertical, Eye, LayoutGrid, List } from "lucide-react";
import { ORDER_KANBAN_COLUMNS as KANBAN_COLUMNS } from "@/lib/statusConfig";

interface OrderWithRelations extends Order {
  customer?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
  };
  trip?: {
    id: string;
    name: string;
    slug: string;
    featuredImage?: string;
  };
  tripDate?: {
    id: string;
    startDate: string;
    endDate: string;
  };
}

export default function OrderKanbanPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [draggedOrder, setDraggedOrder] = useState<OrderWithRelations | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    // Kanban needs the whole board, not a page — pull the max the faceted list allows.
    queryKey: ["orders", "kanban"],
    queryFn: () => api.get<ApiResponse<OrderWithRelations[]>>("/api/admin/orders?limit=100"),
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      return api.patch(`/api/admin/orders/${id}/status`, { status });
    },
    // Optimistic update - immediately move the card
    onMutate: async ({ id, status }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["orders"] });

      // Snapshot the previous value
      const previousOrders = queryClient.getQueryData<{ data: OrderWithRelations[] }>(["orders"]);

      // Optimistically update the cache
      queryClient.setQueryData<{ data: OrderWithRelations[] }>(["orders"], (old) => {
        if (!old) return old;
        return {
          ...old,
          data: old.data.map((order) =>
            order.id === id ? { ...order, status: status as OrderStatus } : order,
          ),
        };
      });

      // Return context with the previous value
      return { previousOrders };
    },
    // If the mutation fails, roll back to the previous value
    onError: (_err, _variables, context) => {
      if (context?.previousOrders) {
        queryClient.setQueryData(["orders"], context.previousOrders);
      }
      // Error is silently handled via rollback
    },
    // Always refetch after error or success
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });

  const orders = data?.data || [];

  // Group orders by status
  const ordersByStatus = KANBAN_COLUMNS.reduce(
    (acc, column) => {
      acc[column.id] = orders.filter((o) => o.status === column.id);
      return acc;
    },
    {} as Record<string, OrderWithRelations[]>,
  );

  // Count cancelled orders separately
  const cancelledCount = orders.filter((o) => o.status === "cancelled").length;

  const handleDragStart = (e: React.DragEvent, order: OrderWithRelations) => {
    setDraggedOrder(order);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", order.id);
  };

  const handleDragOver = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverColumn(columnId);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = (e: React.DragEvent, targetStatus: string) => {
    e.preventDefault();
    setDragOverColumn(null);

    if (draggedOrder && draggedOrder.status !== targetStatus) {
      updateStatusMutation.mutate({
        id: draggedOrder.id,
        status: targetStatus,
      });
    }

    setDraggedOrder(null);
  };

  const handleDragEnd = () => {
    setDraggedOrder(null);
    setDragOverColumn(null);
  };

  if (error) {
    return <div className="card text-error-600 text-center">{t("orders.kanban.loadError")}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("orders.kanban.title")}
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">{t("orders.kanban.subtitle")}</p>
        </div>

        <div className="flex items-center gap-2">
          <Link to="/orders" className="btn-ghost">
            <List className="h-5 w-5" />
            {t("orders.list.tableView")}
          </Link>
          <Link to="/orders/kanban" className="btn-primary">
            <LayoutGrid className="h-5 w-5" />
            {t("orders.list.kanbanView")}
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {KANBAN_COLUMNS.map((column) => (
          <div key={column.id} className="card">
            <div className="flex items-center gap-2">
              <div className={`h-3 w-3 rounded-full ${column.color}`} />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {column.label}
              </span>
            </div>
            <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
              {ordersByStatus[column.id]?.length || 0}
            </p>
          </div>
        ))}
      </div>

      {cancelledCount > 0 && (
        <div className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
          {t("orders.kanban.cancelledHidden", { count: cancelledCount })}
        </div>
      )}

      {/* Kanban Board */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {KANBAN_COLUMNS.map((column) => (
            <div
              key={column.id}
              className={`min-w-[300px] flex-1 rounded-xl ${column.bgColor} p-4 transition-all ${
                dragOverColumn === column.id ? "ring-primary-500 ring-2 ring-offset-2" : ""
              }`}
              onDragOver={(e) => handleDragOver(e, column.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, column.id)}
            >
              {/* Column Header */}
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`h-3 w-3 rounded-full ${column.color}`} />
                  <h3 className="font-semibold text-gray-900 dark:text-white">{column.label}</h3>
                </div>
                <span className="rounded-full bg-white px-2 py-0.5 text-sm font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  {ordersByStatus[column.id]?.length || 0}
                </span>
              </div>

              {/* Cards */}
              <div className="space-y-3">
                {ordersByStatus[column.id]?.map((order) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    isDragging={draggedOrder?.id === order.id}
                    onDragStart={(e) => handleDragStart(e, order)}
                    onDragEnd={handleDragEnd}
                  />
                ))}

                {ordersByStatus[column.id]?.length === 0 && (
                  <div className="rounded-lg border-2 border-dashed border-gray-300 p-6 text-center text-sm text-gray-500 dark:border-gray-600">
                    {t("orders.kanban.emptyColumn")}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface OrderCardProps {
  order: OrderWithRelations;
  isDragging: boolean;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: () => void;
}

function OrderCard({ order, isDragging, onDragStart, onDragEnd }: OrderCardProps) {
  const { t } = useTranslation();
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={`group cursor-grab rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200 transition-all hover:shadow-md active:cursor-grabbing dark:bg-gray-800 dark:ring-gray-700 ${
        isDragging ? "ring-primary-500 opacity-50 ring-2" : ""
      }`}
    >
      {/* Header */}
      <div className="mb-3 flex items-start justify-between">
        <div className="flex items-center gap-2">
          <GripVertical className="h-4 w-4 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100" />
          <div>
            <p className="font-medium text-gray-900 dark:text-white">
              {order.customer?.firstName} {order.customer?.lastName}
            </p>
            <p className="text-xs text-gray-500">{order.customer?.email}</p>
          </div>
        </div>
        <Link
          to={`/orders/${order.id}`}
          className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
          onClick={(e) => e.stopPropagation()}
        >
          <Eye className="h-4 w-4" />
        </Link>
      </div>

      {/* Trip */}
      <div className="mb-3">
        <p className="line-clamp-1 text-sm font-medium text-gray-700 dark:text-gray-300">
          {order.trip?.name}
        </p>
      </div>

      {/* Meta */}
      <div className="flex flex-wrap gap-2 text-xs text-gray-500">
        {order.tripDate && (
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {formatDate(order.tripDate.startDate)}
          </span>
        )}
        <span className="flex items-center gap-1">
          <Users className="h-3 w-3" />
          {t("orders.kanban.ppl", { n: order.adultsCount || 1 })}
        </span>
      </div>

      {/* Price */}
      <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3 dark:border-gray-700">
        <span className="text-xs text-gray-500">{t("orders.kanban.priceLabel")}</span>
        <span className="text-primary-600 font-semibold">{formatPrice(order.totalPrice || 0)}</span>
      </div>
    </div>
  );
}
